import React, { useState, useEffect } from 'react';
import { MapPin, Plus } from 'lucide-react';
import CreateTerritoriesModal from '../../components/CreateTerritoriesModal';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';

interface Territory {
  id: string;
  name: string;
  postal_codes: string[];
}

export default function MobileSettings() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [loading, setLoading] = useState(false);
  const { selectedBusiness } = useBusiness();

  const loadTerritories = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_areas')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTerritories(data || []);
    } catch (err) {
      console.error('Error loading territories:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTerritories();
  }, [selectedBusiness?.id]);

  // Refresh territories when modal is closed
  const handleCloseModal = () => {
    setIsModalOpen(false);
    loadTerritories();
  };

  return (
    <div className="space-y-6 pb-24">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Service Areas</h1>
        <p className="mt-1 text-sm text-gray-500">Configure mobile app and field service settings</p>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-medium text-gray-900">Service Territories</h2>
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Manage Service Territories
            </button>
          </div>
        </div>
        <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">Loading territories...</p>
            </div>
          ) : territories.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">
                No Service Territories
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Add service territories to define your mobile service coverage areas.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {territories.map((territory) => (
                <div key={territory.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900">{territory.name}</h4>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {territory.postal_codes.map((code, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700"
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateTerritoriesModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
}